import React, { createContext, useContext, useMemo, ReactNode, FC, useCallback, useState, useEffect } from 'react';
import { Replacements } from '../types';
import { createDefaultReplacements } from '../BlockElements/BlockKitReplacements/defaultReplacements';

interface BlockKitContextType {
  replacements: Replacements;
  updateReplacements: (newReplacements: Replacements) => void;
}

const defaultContext: BlockKitContextType = {
  replacements: {},
  updateReplacements: () => {},
};

export const BlockKitContext = createContext<BlockKitContextType>(defaultContext);

interface BlockKitProviderProps {
  children: ReactNode;
  replacements?: Replacements;
}

// Explicitly type the component as FC with proper children handling
export const BlockKitProvider: FC<BlockKitProviderProps> = ({ 
  children, 
  replacements: initialReplacements = {} 
}) => {
  // Get default replacements
  const defaultReplacements = useMemo<Replacements>(() => {
    try {
      return createDefaultReplacements();
    } catch (error) {
      console.warn('Error creating default replacements:', error);
      return {};
    }
  }, []);
  
  // Merge default replacements with initial replacements
  const mergedInitialReplacements = useMemo<Replacements>(() => {
    return {
      ...defaultReplacements,
      ...initialReplacements
    };
  }, [defaultReplacements, initialReplacements]);
  
  const [replacements, setReplacements] = useState<Replacements>(mergedInitialReplacements);
  
  // Update state when initialReplacements prop changes
  useEffect(() => {
    setReplacements(prevReplacements => {
      return {
        ...defaultReplacements,
        ...initialReplacements
      };
    });
  }, [initialReplacements, defaultReplacements]);
  
  const updateReplacements = useCallback((newReplacements: Replacements) => {
    setReplacements(prev => ({
      ...prev,
      ...newReplacements
    }));
  }, []);
  
  const value = useMemo(() => ({
    replacements,
    updateReplacements
  }), [replacements]);

  // Use React.Fragment to ensure proper typing
  return (
    <BlockKitContext.Provider value={value}>
      {children as React.ReactNode}
    </BlockKitContext.Provider>
  );
};

// Ensure the display name is set for better debugging
BlockKitProvider.displayName = 'BlockKitProvider';

export const useBlockKit = (): BlockKitContextType => {
  const context = useContext(BlockKitContext);
  if (!context) {
    throw new Error('useBlockKit must be used within a BlockKitProvider');
  }
  return context;
};

export default BlockKitContext;
